import React, { useRef, useState } from "react";

import { cn } from "../../../common/u1/cn";
import { UploadIcon, CloseIcon, DocumentIcon } from "../../../../assets/ui/icons";
import { ACCEPTED_FILE_TYPES, ACCEPTED_FILE_LABEL } from "./supportTickets";

/* The screenshot drop zone. */
export const TicketAttachment = ({
  file,
  onSelect,
  error,
  id = "u1-ticket-file",
  className = "",
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const take = (picked) => {
    if (picked) onSelect(picked);
  };

  return (
    <div className={className}>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[13px] font-semibold text-[var(--ink)] font-sans">
          Upload supporting screenshot
        </span>
        <span
          className={cn(
            "rounded-full bg-[var(--slate-2)] px-2 py-0.5 text-[10px] font-bold",
            "uppercase tracking-[.05em] text-[var(--muted)] font-sans"
          )}
        >
          Optional
        </span>
      </div>

      {file ? (
        <div
          className={cn(
            "relative flex items-center gap-3 rounded-xl border border-[var(--line)]",
            "bg-[var(--bg)] px-4 py-3.5"
          )}
        >
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[var(--coral)]"
          >
            <DocumentIcon size={18} />
          </span>
          <span className="min-w-0 grow basis-0">
            <span className="block truncate text-[13.5px] font-semibold text-[var(--ink)] font-sans">
              {file.name}
            </span>
            <span className="block text-[12px] text-[var(--muted)] font-sans">
              {(file.size / 1024).toFixed(0)} KB
            </span>
          </span>
          <button
            type="button"
            onClick={() => onSelect(null)}
            aria-label="Remove the attached file"
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border",
              "border-[var(--line)] bg-white text-[var(--muted)] transition-colors",
              "hover:border-[var(--danger)] hover:text-[var(--danger)]"
            )}
          >
            <CloseIcon size={14} />
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            id={id}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            className="sr-only"
            onChange={(e) => take(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              take(e.dataTransfer?.files?.[0]);
            }}
            className={cn(
              "flex w-full flex-col items-center justify-center gap-3",
              /* The shared drop-target treatment — see FileDropzone. */
              "rounded-[13px] border-[1.5px] border-dashed px-4 py-9 text-center transition-colors",
              dragging
                ? "border-[var(--coral)] bg-[var(--coral-soft)]"
                : "border-[var(--dropzone-border)] bg-[var(--field-inset)] hover:border-[var(--coral)] hover:bg-[var(--coral-soft)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "flex h-11 w-11 items-center justify-center rounded-xl border",
                "border-[var(--line)] bg-white text-[var(--coral)]"
              )}
            >
              <UploadIcon size={19} />
            </span>
            <span className="text-[13.5px] font-semibold text-[var(--ink)] font-sans">
              Drag &amp; drop or{" "}
              <span className="text-[var(--coral)]">select a file</span> to upload
            </span>
            <span className="text-[12px] text-[var(--muted)] font-sans">
              Supported formats: {ACCEPTED_FILE_LABEL}
            </span>
          </button>
        </>
      )}

      {error && (
        <p
          role="alert"
          className="mt-2 mb-0 text-[12.5px] font-medium text-[var(--danger)] font-sans"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default TicketAttachment;
